import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Why is the app not doing what I expect?
 *
 * Deliberately separate from the readiness probe above it. That answers
 * "should a load balancer send traffic here" — a yes or no, for a machine.
 * This is for a person who cannot read the logs, so every failing row says
 * what to change rather than only that something is off.
 *
 * The checks that matter most are the quiet ones: a worker that stopped so no
 * message is ever sent, an APP_URL still on localhost so every technician link
 * is unopenable, email set to mock. None of those break a page — they simply
 * mean the business stops working while the screens look fine.
 */

const LOOK = {
  ok: {
    icon: CheckCircle2,
    ring: 'border-border',
    tone: 'text-secondary-600',
    label: 'Fine',
  },
  warn: {
    icon: AlertTriangle,
    ring: 'border-accent-200 bg-accent-50/40',
    tone: 'text-accent-600',
    label: 'Worth a look',
  },
  fail: {
    icon: XCircle,
    ring: 'border-danger-200 bg-danger-50/40',
    tone: 'text-danger-600',
    label: 'Broken',
  },
} as const;

export function Diagnostics() {
  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['diagnostics'],
    queryFn: api.diagnostics,
    // Not on a timer. These run real queries, and a page that re-checks every
    // few seconds is a page nobody leaves open.
    staleTime: 30_000,
  });

  if (isPending) {
    return (
      <section className="card flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Checking" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="card">
        <h3 className="font-heading text-lg">Diagnostics</h3>
        <p className="mt-2 text-sm text-danger-700">{(error as Error).message}</p>
      </section>
    );
  }

  const problems = data.checks.filter((c) => c.status !== 'ok').length;

  return (
    <section className="card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg">What might be wrong</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {problems === 0
              ? 'Nothing needs attention.'
              : `${problems} thing${problems === 1 ? '' : 's'} worth looking at.`}
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary text-sm"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
            aria-hidden="true"
          />
          Check again
        </button>
      </div>

      <ul className="mt-4 space-y-2">
        {/* Problems first. A list that buries the broken row under six green
            ones makes somebody read all seven to find it. */}
        {[...data.checks]
          .sort((a, b) => {
            const rank = { fail: 0, warn: 1, ok: 2 } as const;
            return rank[a.status] - rank[b.status];
          })
          .map((check) => {
            const look = LOOK[check.status];
            const Icon = look.icon;
            return (
              <li key={check.id} className={`rounded-xl border p-3 ${look.ring}`}>
                <div className="flex items-start gap-2.5">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${look.tone}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {check.label}
                      {/* The word, not only the colour — this has to read the
                          same to somebody who cannot separate red from amber. */}
                      <span className={`text-xs font-normal ${look.tone}`}>{look.label}</span>
                    </p>
                    <p className="mt-0.5 text-sm text-muted-foreground">{check.detail}</p>
                    {check.fix && (
                      <p className="mt-1.5 rounded-lg bg-card px-2.5 py-1.5 text-sm">
                        <span className="font-medium">What to do: </span>
                        {check.fix}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
      </ul>

      <p className="mt-3 text-xs text-muted-foreground">
        Checked {new Date(data.checkedAt).toLocaleString('en-KE')}. Everything here is read-only —
        running it changes nothing.
      </p>
    </section>
  );
}
